<template>
  <div class="ibox">
    <div class="ibox-title">
      <h2>Load Planner</h2>
    </div>
    <div class="ibox-content">

      <ul class="list-detail list-detail-planner inline-form-container" v-sortable="{ sorted: this.handlePlannedLoadsSorted }">
        <planned-load
          v-for="(load, index) in plannedLoadsWithoutEmpty"
          :load="plannedLoadsWithoutEmpty[index]"
          :key="load.id"
          :data-sortable-id="load.id"
          :default-carriers="carriers">
        </planned-load>
        <li v-if="noPlannedLoads" class="text-center text-muted">
          There are no planned loads.
        </li>
      </ul>

    </div>

    <div class="ibox-footer">
      <h4 class="text-upper">Add Load:</h4>
      <ul class="list-detail list-detail-planner inline-form-container">
        <planned-load :load="emptyPlannedLoad" :default-carriers="carriers"></planned-load>
      </ul>
    </div>

    <div class="ibox-footer">
      <div class="row">
        <div class="col-xs-6 col-md-3 col-lg-3">
          <p class="load-detail">Job Rate: {{ jobDecorated.rate }}</p>
        </div>
        <div class="col-xs-6 col-md-4 col-lg-4">
          <p class="load-detail">
            Average Rate: {{ averageLoadRateAsMoney }}
            <span class="small" :class="{ 'text-success': averageIsProfitable, 'text-danger': averageIsNotProfitable }">
              ({{ averageProfitPercentage }}%)
            </span>
          </p>
        </div>
        <div class="col-xs-12 col-md-5 col-lg-5">
          <p class="assigned">Planned: {{ plannedLoadSize }} of {{ jobSize }} tonnes. ({{ plannedLoadPercentage }}%)</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
  import { ADD_LOAD, REMOVE_LOAD, SET_LOAD_ATTRIBUTE } from 'vx/mutations'
  import { clone, sumBy, each } from 'lodash'
  import PlannedLoad from './PlannedLoad'
  import accounting from 'accounting'

  export default {
    components: {
      PlannedLoad
    },

    data () {
      return {
        carriers: []
      }
    },

    created () {
      this.getPlannedLoads()

      // Get the "initial" carriers that we'll store on our local state as a cloned copy.
      // We use this for all new loads that get added and for loads that do not have
      // a carrier selected yet. This saves us requesting the carries for each load.
      this.getCarriers({ verified: true }).then(carriers => {
        this.carriers = clone(carriers)
      })
    },

    computed: {
      ...mapGetters('job', ['jobDecorated']),
      ...mapState('job', ['job']),
      ...mapState('job/plannedLoads', ['plannedLoads']),
      ...mapGetters('job/plannedLoads', [
        'plannedLoadsWithoutEmpty',
        'emptyPlannedLoad'
      ]),

      /**
       * Determine if we have no planned loads.
       *
       * @return boolean
       */
      noPlannedLoads () {
        return this.plannedLoadsWithoutEmpty.length === 0
      },

      /**
       * Get the job size.
       *
       * @return float
       */
      jobSize () {
        return accounting.formatNumber(this.job.quantity, 2)
      },

      /**
       * Get the planned load size.
       *
       * @return float
       */
      plannedLoadSize () {
        return accounting.formatNumber(sumBy(this.plannedLoadsWithoutEmpty, load => parseFloat(load.size) || 0), 2)
      },

      /**
       * Get the planned load percentage of the total job quantity.
       *
       * @return float
       */
      plannedLoadPercentage () {
        if (this.job.quantity === 0 || this.job.quantity === null) {
          return 0
        }

        return Math.round((this.plannedLoadSize / this.job.quantity) * 100)
      },

      /**
       * Get the average load rate.
       *
       * @return float
       */
      averageLoadRate () {
        if (this.plannedLoadsWithoutEmpty.length === 0) {
          return 0
        }

        return sumBy(this.plannedLoadsWithoutEmpty, load => (parseFloat(load.rate) || 0)) / this.plannedLoadsWithoutEmpty.length
      },

      /**
       * Get the average load rate as formatted money.
       *
       * @return string
       */
      averageLoadRateAsMoney () {
        return `${accounting.formatMoney(this.averageLoadRate)}/t`
      },

      /**
       * Get the average profit as a percentage.
       *
       * @return float
       */
      averageProfitPercentage () {
        if (this.averageLoadRate === 0) {
          return 0
        }

        return (parseFloat(((this.job.rate - this.averageLoadRate) / this.averageLoadRate) * 100) || 0).toFixed(2)
      },

      /**
       * Determine if average is profitable.
       *
       * @return boolean
       */
      averageIsProfitable () {
        return this.averageProfitPercentage > 0
      },

      /**
       * Determine if average is not profitable.
       *
       * @return boolean
       */
      averageIsNotProfitable () {
        return this.averageProfitPercentage <= 0
      }
    },

    methods: {
      ...mapActions('carrier', ['getCarriers']),
      ...mapActions('job', ['getJob', 'updatePlannedLoadOrder']),
      ...mapActions('job/plannedLoads', ['getPlannedLoads']),
      ...mapMutations('job/plannedLoads', [ADD_LOAD, REMOVE_LOAD, SET_LOAD_ATTRIBUTE]),

      /**
       * Handle sorting of the planned loads.
       *
       * @param object loads
       *
       * @return Promise
       */
      handlePlannedLoadsSorted (loads) {
        const updatePlannedLoadState = () => {
          each(loads, ({ id, order }) => this[SET_LOAD_ATTRIBUTE]({ id, attribute: 'order', value: order }))
        }

        return this.updatePlannedLoadOrder({ id: this.job.id, payload: { loads } }).then(updatePlannedLoadState)
      }
    }
  }
</script>



// WEBPACK FOOTER //
// LoadPlanner.vue?0ed6c7e4